/**
 * Workflow Dashboard Component
 * 
 * Displays prime artifacts grouped by workflow state (IDENTIFIED, DEFINED, IMPLEMENTED, VALIDATED)
 * Shows progress and allows navigation to artifact details
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, List, Badge, Input, Spin, message } from 'antd';
import { 
  CheckCircleOutlined, 
  EditOutlined, 
  ToolOutlined, 
  FileAddOutlined,
  SearchOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { ArtifactService } from '../../services/api/artifactService';
import styles from './WorkflowDashboard.module.css';

const { Search } = Input;

interface Artifact {
  id: string;
  name: string;
  description: string;
  artifact_type_id: string;
  is_template_ready: boolean;
  created_at: string;
  updated_at: string;
}

interface WorkflowMetrics {
  IDENTIFIED: number;
  DEFINED: number;
  IMPLEMENTED: number;
  VALIDATED: number;
  TOTAL: number;
}

interface WorkflowData {
  metrics: WorkflowMetrics;
  artifacts_by_state: {
    IDENTIFIED: Artifact[];
    DEFINED: Artifact[];
    IMPLEMENTED: Artifact[];
    VALIDATED: Artifact[];
  };
  workflow_states: string[];
}

const WORKFLOW_CONFIG = {
  IDENTIFIED: {
    title: 'Identified',
    icon: <FileAddOutlined />,
    color: '#d9d9d9',
    badgeStatus: 'default' as const,
    description: 'Requires: Details & States approved'
  },
  DEFINED: {
    title: 'Defined',
    icon: <EditOutlined />,
    color: '#1890ff',
    badgeStatus: 'processing' as const,
    description: 'Requires: Transitions approved'
  },
  IMPLEMENTED: {
    title: 'Implemented',
    icon: <ToolOutlined />,
    color: '#faad14',
    badgeStatus: 'warning' as const,
    description: 'Requires: Process Artifacts approved'
  },
  VALIDATED: {
    title: 'Validated',
    icon: <CheckCircleOutlined />,
    color: '#52c41a',
    badgeStatus: 'success' as const,
    description: 'Production ready - Template complete'
  }
};

interface WorkflowDashboardProps {
  onArtifactClick?: (artifactId: string) => void;
  modelType?: 'prime' | 'company';
  artifactTreeId?: string;
  companyId?: string;
}

export const WorkflowDashboard: React.FC<WorkflowDashboardProps> = ({
  onArtifactClick,
  modelType = 'prime',
  artifactTreeId,
  companyId
}) => {
  const [loading, setLoading] = useState(true);
  const [workflowData, setWorkflowData] = useState<WorkflowData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadWorkflowData();
  }, [modelType, artifactTreeId, companyId]);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      
      if (modelType === 'company') {
        // For company artifacts, we need to fetch company artifacts and group them
        // Since there's no direct company workflow-status endpoint, we'll fetch artifacts
        // and group them manually or use a different approach
        try {
          const artifacts = await ArtifactService.getArtifacts(
            'company',
            artifactTreeId,
            undefined,
            companyId
          );
          
          // Build workflow data structure from company artifacts
          // Company artifacts may not have workflow states like prime artifacts,
          // so we'll create a simplified structure
          const workflowData: WorkflowData = {
            metrics: {
              IDENTIFIED: 0,
              DEFINED: 0,
              IMPLEMENTED: 0,
              VALIDATED: 0,
              TOTAL: artifacts.length
            },
            artifacts_by_state: {
              IDENTIFIED: [],
              DEFINED: [],
              IMPLEMENTED: [],
              VALIDATED: []
            },
            workflow_states: ['IDENTIFIED', 'DEFINED', 'IMPLEMENTED', 'VALIDATED']
          };
          
          // For now, all company artifacts go into IDENTIFIED since they don't have workflow states
          // This can be enhanced later when company artifacts have workflow states
          workflowData.artifacts_by_state.IDENTIFIED = artifacts.map((a: any) => ({
            id: a.id,
            name: a.name,
            description: a.description || '',
            artifact_type_id: a.artifact_type_id || '',
            is_template_ready: false,
            created_at: a.created_at || '',
            updated_at: a.updated_at || ''
          }));
          workflowData.metrics.IDENTIFIED = artifacts.length;
          workflowData.metrics.TOTAL = artifacts.length;
          
          setWorkflowData(workflowData);
        } catch (error: any) {
          console.error('Error loading company workflow data:', error);
          message.error(`Failed to load company workflow data: ${error.message}`);
        }
      } else {
        // Prime artifacts - use existing endpoint
        const response = await apiClient.get('/prime-artifacts/workflow-status');
        setWorkflowData(response.data);
      }
    } catch (error: any) {
      message.error(`Failed to load workflow data: ${error.message}`);
      console.error('Error loading workflow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArtifacts = (artifacts: Artifact[]) => {
    if (!searchTerm) return artifacts;
    
    const term = searchTerm.toLowerCase();
    return artifacts.filter(artifact =>
      artifact.name.toLowerCase().includes(term) ||
      (artifact.description && artifact.description.toLowerCase().includes(term))
    );
  };

  const handleArtifactClick = (artifactId: string) => {
    if (onArtifactClick) {
      onArtifactClick(artifactId);
    } else {
      // Default navigation
      window.location.href = `/prime-artifacts/${artifactId}`;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        <div style={{ marginTop: 16 }}>Loading workflow data...</div>
      </div>
    );
  }

  if (!workflowData) {
    return <div>No workflow data available</div>;
  }

  return (
    <div className={styles.workflowDashboard}>
      <div className={styles.workflowDashboardHeader}>
        <h2>{modelType === 'company' ? 'Company Model Workflow' : 'Prime Model Development Workflow'}</h2>
        <p>{modelType === 'company' ? 'Track company artifacts' : 'Track progress of prime artifacts through development stages'}</p>
        
        <Search
          placeholder="Search artifacts..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 300, marginTop: 16 }}
          allowClear
        />
      </div>

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Artifacts"
              value={workflowData.metrics.TOTAL}
              prefix={<FileAddOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Identified"
              value={workflowData.metrics.IDENTIFIED}
              valueStyle={{ color: WORKFLOW_CONFIG.IDENTIFIED.color }}
              prefix={WORKFLOW_CONFIG.IDENTIFIED.icon}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Progress"
              value={workflowData.metrics.DEFINED + workflowData.metrics.IMPLEMENTED}
              valueStyle={{ color: WORKFLOW_CONFIG.IMPLEMENTED.color }}
              prefix={<ToolOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Validated"
              value={workflowData.metrics.VALIDATED}
              valueStyle={{ color: WORKFLOW_CONFIG.VALIDATED.color }}
              prefix={WORKFLOW_CONFIG.VALIDATED.icon}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 24 }}>
        {(Object.keys(WORKFLOW_CONFIG) as Array<keyof typeof WORKFLOW_CONFIG>).map((state) => {
          const config = WORKFLOW_CONFIG[state];
          // Filter and sort artifacts
          const filteredArtifacts = filterArtifacts(workflowData.artifacts_by_state[state] || []);
          const sortedArtifacts = [...filteredArtifacts].sort((a, b) => 
            a.name.localeCompare(b.name)
          );
          const percentage = workflowData.metrics.TOTAL > 0
            ? ((workflowData.metrics[state] / workflowData.metrics.TOTAL) * 100).toFixed(0)
            : 0;

          return (
            <Col span={6} key={state}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: config.color }}>{config.icon}</span>
                    <span>{config.title}</span>
                    <Badge count={sortedArtifacts.length} style={{ backgroundColor: config.color }} />
                  </div>
                }
                extra={<span style={{ fontSize: '14px', color: '#888' }}>{percentage}%</span>}
                className={styles.workflowColumnCard}
                styles={{ header: { backgroundColor: `${config.color}15`, borderBottom: `2px solid ${config.color}` } }}
              >
                <p style={{ color: '#666', fontSize: '12px', marginBottom: 16 }}>
                  {config.description}
                </p>
                
                <List
                  dataSource={sortedArtifacts}
                  locale={{ emptyText: 'No artifacts in this state' }}
                  renderItem={(artifact) => (
                    <List.Item
                      className={styles.workflowArtifactItem}
                      onClick={() => handleArtifactClick(artifact.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <List.Item.Meta
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{artifact.name}</span>
                            {artifact.is_template_ready && (
                              <Badge status="success" text="Ready" />
                            )}
                          </div>
                        }
                        description={
                          artifact.description
                            ? artifact.description.substring(0, 60) + (artifact.description.length > 60 ? '...' : '')
                            : 'No description'
                        }
                      />
                    </List.Item>
                  )}
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                />
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

